import React from 'react';

import delve from 'dlv';

import {
  ICombo,
  IComboSelectionCount,
  IComboSlot,
  IComboSlotOption,
  IComboSlotSelection,
  IComboSlotSelections,
  IItem,
  IItemOption,
  IModifierSelection,
  IPicker,
} from '@rbi-ctg/menu';
import Expander from 'components/expander';
import { useMenuContext } from 'state/menu';
import { assignInclusionComboSlotSelections } from 'utils/menu';

import ComboSlotDetailsContainer from './combo-slot-details-container';
import ComboSlotOption from './combo-slot-option';
import Modifiers from './modifiers';
import {
  HandleComboSelection,
  HandleComboSlotSelectionQuantityUpdate,
  HandleModifierSelection,
  HandleQuantityUpdate,
} from './types';

const OptionsWrapper: React.FC<{ comboSlot: IComboSlot }> = ({ comboSlot, children }) => {
  const visibleChildren = delve(comboSlot, 'optionVisibilitySettings.visibleOptions');

  if (visibleChildren) {
    return (
      <Expander
        minVisibleChildren={visibleChildren}
        openText={delve(comboSlot, 'optionVisibilitySettings.toggleButtonTextOpen.locale', '')}
        closedText={delve(comboSlot, 'optionVisibilitySettings.toggleButtonTextClosed.locale', '')}
      >
        {children}
      </Expander>
    );
  } else {
    // Fragment needed to satisfy TS
    return <>{children}</>;
  }
};

interface IComboSlotDetailProps {
  combo: ICombo;
  comboSlot: IComboSlot;
  comboSlotSelections: IComboSlotSelections;
  handleComboSelection: HandleComboSelection;
  handleComboSlotSelectionQuantityUpdate: HandleComboSlotSelectionQuantityUpdate;
  handleModifierQuantityUpdate: HandleQuantityUpdate;
  handleModifierSelection: HandleModifierSelection;
  modifierSelections: IModifierSelection[];
}

const ComboSlotDetail: React.FC<IComboSlotDetailProps> = ({
  combo,
  comboSlot,
  comboSlotSelections,
  handleComboSelection,
  handleComboSlotSelectionQuantityUpdate,
  handleModifierQuantityUpdate,
  handleModifierSelection,
  modifierSelections,
}) => {
  const { priceForComboSlotSelection } = useMenuContext();

  const comboSlotMinAmount = comboSlot ? comboSlot.minAmount : 1;
  const comboSlotMaxAmount = comboSlot ? comboSlot.maxAmount : 1;

  const comboSlotQuantitiesAreTheSame = comboSlotMinAmount === comboSlotMaxAmount;
  const optionQuantitiesAreTheSame = comboSlot.options.every(option => {
    return (
      option.minAmount === option.maxAmount &&
      option.minAmount === option.defaultAmount &&
      option.maxAmount === option.defaultAmount
    );
  });

  const isReadOnly =
    (comboSlotQuantitiesAreTheSame && optionQuantitiesAreTheSame) ||
    comboSlot.uiPattern === 'locked';

  const handleChange = (
    option: IComboSlotOption,
    selectionCount: IComboSelectionCount,
    respectMaximum: boolean
  ) => {
    handleComboSelection(comboSlot._id, option, selectionCount, respectMaximum);
  };

  const handleQuantityUpdate = (option: IItem, quantity: number) => {
    handleComboSlotSelectionQuantityUpdate(comboSlot._id, option, quantity);
  };

  const selectionsForComboSlot = comboSlotSelections[comboSlot._id];

  const currentSelections = selectionsForComboSlot
    ? assignInclusionComboSlotSelections(selectionsForComboSlot.selections, comboSlotMaxAmount)
    : [];

  const totalSelections =
    currentSelections &&
    currentSelections.reduce(
      (total: number, selection: { quantity: number }) => total + selection.quantity,
      0
    );

  // only display modifier selections when a combo slot's
  // max amount is less than or equal to 1
  const shouldDisplayModifiers = comboSlotMaxAmount <= 1;

  let currentItem: IComboSlotSelection | null | undefined;
  let selectedOption: IItem | IPicker | undefined;

  return (
    <ComboSlotDetailsContainer
      title={comboSlot.name?.locale}
      requirementSatisfied={totalSelections >= comboSlotMinAmount}
      required
    >
      <OptionsWrapper comboSlot={comboSlot}>
        {comboSlot.options.map((csOption: IComboSlotOption) => {
          const { option } = csOption;

          // if there is no option this combo slot option
          // cannot be rendered
          if (!option) {
            return null;
          }

          const selectedItem: typeof currentItem = currentSelections
            ? currentSelections.find(
                (selection: { option: IComboSlotOption }) =>
                  selection.option.option._id === option._id
              )
            : null;

          if (selectedItem) {
            currentItem = selectedItem;
            selectedOption = option;
          }

          const selectedItemPrice = priceForComboSlotSelection({
            combo,
            comboSlot,
            selectedOption: selectedItem?.option,
          });

          return (
            <React.Fragment key={option._id}>
              <ComboSlotOption
                comboSlot={comboSlot._id}
                isReadOnly={isReadOnly}
                option={option as IItem}
                handleQuantityUpdate={handleQuantityUpdate}
                onChange={respectMaximum =>
                  handleChange(
                    csOption,
                    {
                      min: comboSlotMinAmount,
                      max: comboSlotMaxAmount,
                    },
                    respectMaximum
                  )
                }
                item={selectedItem}
                extraCharge={selectedItemPrice}
                isPremium={selectedItem?.option.isPremium ?? false}
                respectMaximum={comboSlot?.respectMaximum}
                maxAmount={comboSlot?.maxAmount}
              />
            </React.Fragment>
          );
        })}
        {currentItem && selectedOption && shouldDisplayModifiers && (
          <Modifiers
            comboSlotId={comboSlot._id}
            item={selectedOption as IItem}
            modifiers={(selectedOption?.options || []) as IItemOption[]}
            radioGroupParentId={selectedOption?._id}
            modifierSelections={modifierSelections.filter(
              ({ comboSlotId }) => comboSlotId === comboSlot._id
            )}
            handleModifierSelection={handleModifierSelection}
            handleModifierQuantityUpdate={handleModifierQuantityUpdate}
          />
        )}
      </OptionsWrapper>
    </ComboSlotDetailsContainer>
  );
};

export default ComboSlotDetail;
